import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["renderLabel", "value", "placeholder", "onBlur", "isRequired", "size", "isInline", "layout", "width", "onRequestValidateDate", "onRequestShowCalendar", "onRequestHideCalendar", "onRequestSelectNextDay", "onRequestSelectPrevDay", "onRequestRenderNextMonth", "onRequestRenderPrevMonth"],
  _excluded2 = ["ref"];
var _dec, _class, _DateInput, _IconCalendarMonthLin;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Children, Component } from 'react';
import { Calendar } from '@instructure/ui-calendar';
import { IconCalendarMonthLine } from '@instructure/ui-icons';
import { Popover } from '@instructure/ui-popover';
import { Selectable } from '@instructure/ui-selectable';
import { TextInput } from '@instructure/ui-text-input';
import { createChainedFunction } from '@instructure/ui-utils';
import { getInteraction, callRenderProp, safeCloneElement, passthroughProps } from '@instructure/ui-react-utils';
import { DateTime, Locale } from '@instructure/ui-i18n';
import { withStyle } from '@instructure/emotion';
import generateStyle from "./styles.js";
import { allowedProps } from "./props.js";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
category: components
---
**/
let DateInput = (_dec = withStyle(generateStyle, null), _dec(_class = (_DateInput = class DateInput extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      hasInputRef: false,
      isShowingCalendar: false,
      validatedDate: void 0,
      messages: []
    };
    this._input = void 0;
    this.ref = null;
    this.formatDateId = date => {
      // ISO8601 strings may contain a space. Remove any spaces before using the
      // date as the id.
      return date.replace(/\s/g, '');
    };
    this.handleInputRef = el => {
      var _this$props$inputRef, _this$props;
      // Ensures that we position the Calendar with respect to the input correctly
      // if the Calendar is open on mount
      if (!this.state.hasInputRef) {
        this.setState({
          hasInputRef: true
        });
      }
      this._input = el;
      (_this$props$inputRef = (_this$props = this.props).inputRef) === null || _this$props$inputRef === void 0 ? void 0 : _this$props$inputRef.call(_this$props, el);
    };
    this.handleInputChange = (event, value) => {
      var _this$props$onChange, _this$props2;
      (_this$props$onChange = (_this$props2 = this.props).onChange) === null || _this$props$onChange === void 0 ? void 0 : _this$props$onChange.call(_this$props2, event, {
        value
      });
      this.handleShowCalendar(event);
    };
    this.handleShowCalendar = event => {
      if (!this.props.children) {
        this.setState({
          isShowingCalendar: true
        });
      } else if (this.interaction === 'enabled' && this.props.children) {
        var _this$props$onRequest, _this$props3;
        (_this$props$onRequest = (_this$props3 = this.props).onRequestShowCalendar) === null || _this$props$onRequest === void 0 ? void 0 : _this$props$onRequest.call(_this$props3, event);
      }
    };
    this.validateDate = date => {
      const invalidDateErrorMessage = this.props.invalidDateErrorMessage;
      const disabledDateErrorMessage = this.props.disabledDateErrorMessage || invalidDateErrorMessage;
      const messages = [];
      // check if date is enabled
      const disabledDates = this.props.disabledDates;
      if (typeof disabledDates === 'function' && disabledDates(date) || Array.isArray(disabledDates) && disabledDates.find(dateString => DateTime.parse(dateString, this.locale(), this.timezone()).isSame(DateTime.parse(date, this.locale(), this.timezone()), 'day'))) {
        messages.push(typeof disabledDateErrorMessage === 'function' ? disabledDateErrorMessage(date) : {
          type: 'error',
          text: disabledDateErrorMessage
        });
      }

      // check if date is valid
      if (!DateTime.parse(date, this.locale(), this.timezone(), [DateTime.momentISOFormat, 'llll', 'LLLL', 'lll', 'LLL', 'll', 'LL', 'l', 'L'], true).isValid()) {
        messages.push(typeof invalidDateErrorMessage === 'function' ? invalidDateErrorMessage(date) : {
          type: 'error',
          text: invalidDateErrorMessage
        });
      }
      return messages;
    };
    this.handleHideCalendar = (event, setectedDate) => {
      if (!this.props.children) {
        const dateString = setectedDate || this.props.value;
        const messages = [];
        if (this.props.onRequestValidateDate) {
          var _this$props$onRequest2, _this$props4;
          const userValidatedDate = (_this$props$onRequest2 = (_this$props4 = this.props).onRequestValidateDate) === null || _this$props$onRequest2 === void 0 ? void 0 : _this$props$onRequest2.call(_this$props4, event, dateString || '', this.validateDate(dateString || ''));
          messages.push(...(userValidatedDate || []));
        } else {
          if (dateString) {
            messages.push(...this.validateDate(dateString));
          }
        }
        this.setState({
          messages,
          isShowingCalendar: false
        });
      } else {
        var _this$props$onRequest3, _this$props5, _this$props$onRequest4, _this$props6;
        (_this$props$onRequest3 = (_this$props5 = this.props).onRequestValidateDate) === null || _this$props$onRequest3 === void 0 ? void 0 : _this$props$onRequest3.call(_this$props5, event);
        (_this$props$onRequest4 = (_this$props6 = this.props).onRequestHideCalendar) === null || _this$props$onRequest4 === void 0 ? void 0 : _this$props$onRequest4.call(_this$props6, event);
      }
    };
    this.handleHighlightOption = (event, {
      direction
    }) => {
      const _this$props7 = this.props,
        onRequestSelectNextDay = _this$props7.onRequestSelectNextDay,
        onRequestSelectPrevDay = _this$props7.onRequestSelectPrevDay,
        onChange = _this$props7.onChange,
        value = _this$props7.value,
        currentDate = _this$props7.currentDate;
      const isValueValid = value && DateTime.parse(value, this.locale(), this.timezone()).isValid();
      if (direction === -1) {
        if (onRequestSelectPrevDay) {
          onRequestSelectPrevDay === null || onRequestSelectPrevDay === void 0 ? void 0 : onRequestSelectPrevDay(event);
        } else {
          // @ts-expect-error TODO
          onChange(event, {
            value: DateTime.parse(isValueValid ? value : currentDate, this.locale(), this.timezone()).subtract(1, 'day').format('MMMM D, YYYY')
          });
          this.setState({
            messages: []
          });
        }
      }
      if (direction === 1) {
        if (onRequestSelectNextDay) {
          onRequestSelectNextDay === null || onRequestSelectNextDay === void 0 ? void 0 : onRequestSelectNextDay(event);
        } else {
          // @ts-expect-error TODO
          onChange(event, {
            value: DateTime.parse(isValueValid ? value : currentDate, this.locale(), this.timezone()).add(1, 'day').format('MMMM D, YYYY')
          });
          this.setState({
            messages: []
          });
        }
      }
    };
    this.shouldShowCalendar = () => this.props.children ? this.props.isShowingCalendar : this.state.isShowingCalendar;
  }
  locale() {
    if (this.props.locale) {
      return this.props.locale;
    } else if (this.context && this.context.locale) {
      return this.context.locale;
    }
    return Locale.browserLocale();
  }
  timezone() {
    if (this.props.timezone) {
      return this.props.timezone;
    } else if (this.context && this.context.timezone) {
      return this.context.timezone;
    }
    return DateTime.browserTimeZone();
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props8;
    (_this$props$makeStyle = (_this$props8 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props8);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props9;
    (_this$props$makeStyle2 = (_this$props9 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props9);
  }
  get selectedDateId() {
    let selectedDateId;
    Children.toArray(this.props.children).forEach(day => {
      const _props = day.props,
        date = _props.date,
        isSelected = _props.isSelected;
      if (isSelected) {
        selectedDateId = this.formatDateId(date);
      }
    });
    return selectedDateId;
  }
  get interaction() {
    return getInteraction({
      props: this.props
    });
  }
  renderMonthNavigationButton(type = 'prev') {
    const _this$props0 = this.props,
      renderPrevMonthButton = _this$props0.renderPrevMonthButton,
      renderNextMonthButton = _this$props0.renderNextMonthButton;
    const button = type === 'prev' ? renderPrevMonthButton : renderNextMonthButton;
    return button && safeCloneElement(callRenderProp(button), {
      tabIndex: -1
    });
  }
  renderDays(getOptionProps) {
    const children = this.props.children;
    if (!children) return;
    return Children.map(children, day => {
      const _day$props = day.props,
        date = _day$props.date,
        isOutsideMonth = _day$props.isOutsideMonth;
      const props = {
        tabIndex: -1,
        id: this.formatDateId(date)
      };
      const optionProps = getOptionProps(props);
      const propsAdded = isOutsideMonth ? {
        ...props,
        onClick: optionProps.onClick,
        role: 'presentation'
      } : optionProps;
      return safeCloneElement(day, propsAdded);
    });
  }
  renderCalendar({
    getListProps,
    getOptionProps
  }) {
    const _this$props1 = this.props,
      onRequestRenderNextMonth = _this$props1.onRequestRenderNextMonth,
      onRequestRenderPrevMonth = _this$props1.onRequestRenderPrevMonth,
      renderNavigationLabel = _this$props1.renderNavigationLabel,
      renderWeekdayLabels = _this$props1.renderWeekdayLabels,
      value = _this$props1.value,
      onChange = _this$props1.onChange,
      disabledDates = _this$props1.disabledDates,
      currentDate = _this$props1.currentDate;
    const isValidDate = value ? DateTime.parse(value, this.locale(), this.timezone()).isValid() : false;
    const noChildrenProps = this.props.children ? {} : {
      disabledDates,
      currentDate,
      selectedDate: isValidDate ? value : void 0,
      visibleMonth: isValidDate ? value : void 0,
      onDateSelected: (dateString, momentDate, e) => {
        // @ts-expect-error TODO
        onChange === null || onChange === void 0 ? void 0 : onChange(e, {
          value: `${momentDate.format('MMMM')} ${momentDate.format('D')}, ${momentDate.format('YYYY')}`
        });
        this.handleHideCalendar(e, dateString);
      }
    };
    return _jsx(Calendar, {
      ...getListProps({
        onRequestRenderNextMonth,
        onRequestRenderPrevMonth,
        renderNavigationLabel,
        renderWeekdayLabels,
        renderNextMonthButton: this.renderMonthNavigationButton('next'),
        renderPrevMonthButton: this.renderMonthNavigationButton('prev')
      }),
      ...noChildrenProps,
      children: this.renderDays(getOptionProps)
    });
  }
  renderInput({
    getInputProps,
    getTriggerProps
  }) {
    const _this$props10 = this.props,
      renderLabel = _this$props10.renderLabel,
      value = _this$props10.value,
      placeholder = _this$props10.placeholder,
      onBlur = _this$props10.onBlur,
      isRequired = _this$props10.isRequired,
      size = _this$props10.size,
      isInline = _this$props10.isInline,
      layout = _this$props10.layout,
      width = _this$props10.width,
      onRequestValidateDate = _this$props10.onRequestValidateDate,
      onRequestShowCalendar = _this$props10.onRequestShowCalendar,
      onRequestHideCalendar = _this$props10.onRequestHideCalendar,
      onRequestSelectNextDay = _this$props10.onRequestSelectNextDay,
      onRequestSelectPrevDay = _this$props10.onRequestSelectPrevDay,
      onRequestRenderNextMonth = _this$props10.onRequestRenderNextMonth,
      onRequestRenderPrevMonth = _this$props10.onRequestRenderPrevMonth,
      rest = _objectWithoutProperties(_this$props10, _excluded);
    const interaction = this.interaction;
    const _getTriggerProps = getTriggerProps(),
      ref = _getTriggerProps.ref,
      triggerProps = _objectWithoutProperties(_getTriggerProps, _excluded2);
    const messages = this.props.messages || this.state.messages;
    return _jsx(TextInput, {
      ...triggerProps,
      ...passthroughProps(rest),
      ...getInputProps({
        renderLabel: callRenderProp(renderLabel),
        value,
        placeholder,
        size,
        layout,
        width,
        messages,
        onChange: this.handleInputChange,
        onBlur: createChainedFunction(onBlur, this.handleHideCalendar),
        inputRef: createChainedFunction(ref, this.handleInputRef),
        interaction,
        isRequired,
        display: isInline ? 'inline-block' : 'block',
        renderAfterInput: _IconCalendarMonthLin || (_IconCalendarMonthLin = _jsx(IconCalendarMonthLine, {
          inline: false
        }))
      }),
      onKeyDown: e => {
        var _triggerProps$onKeyDo;
        if (!this.props.children) {
          if (e.key === 'Enter') {
            // @ts-expect-error TODO
            this.handleHideCalendar(e);
          }
        }
        (_triggerProps$onKeyDo = triggerProps.onKeyDown) === null || _triggerProps$onKeyDo === void 0 ? void 0 : _triggerProps$onKeyDo.call(triggerProps, e);
      }
    });
  }
  render() {
    const _this$props11 = this.props,
      placement = _this$props11.placement,
      assistiveText = _this$props11.assistiveText,
      styles = _this$props11.styles;
    const isShowingCalendar = this.shouldShowCalendar();
    return _jsx(Selectable, {
      isShowingOptions: isShowingCalendar,
      onRequestShowOptions: this.handleShowCalendar,
      onRequestHideOptions: this.handleHideCalendar,
      onRequestHighlightOption: this.handleHighlightOption,
      onRequestSelectOption: e => this.handleHideCalendar(e),
      selectedOptionId: this.selectedDateId,
      highlightedOptionId: this.selectedDateId,
      children: ({
        getRootProps,
        getInputProps,
        getTriggerProps,
        getListProps,
        getOptionProps,
        getDescriptionProps
      }) => _jsxs("span", {
        ...getRootProps({
          css: styles === null || styles === void 0 ? void 0 : styles.dateInput
        }),
        ref: el => {
          this.ref = el;
        },
        "data-cid": "DateInput",
        children: [this.renderInput({
          getInputProps,
          getTriggerProps
        }), _jsx("span", {
          ...getDescriptionProps(),
          css: styles === null || styles === void 0 ? void 0 : styles.assistiveText,
          children: assistiveText
        }), _jsx(Popover, {
          placement: placement,
          isShowingContent: isShowingCalendar,
          positionTarget: this._input,
          shouldReturnFocus: false,
          shouldFocusContentOnTriggerBlur: true,
          children: this.renderCalendar({
            getListProps,
            getOptionProps
          })
        })]
      })
    });
  }
}, _DateInput.displayName = "DateInput", _DateInput.componentId = 'DateInput', _DateInput.Day = Calendar.Day, _DateInput.allowedProps = allowedProps, _DateInput.defaultProps = {
  value: '',
  size: 'medium',
  onBlur: () => {},
  // must have a default so createChainedFunction works
  isRequired: false,
  isInline: false,
  layout: 'stacked',
  display: 'inline-block',
  placement: 'bottom center',
  isShowingCalendar: false
}, _DateInput)) || _class);
export default DateInput;
export { DateInput };